import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Check,
  X,
  Circle,
  Shield,
  Download,
  Hash,
  FileSearch,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DownloadEmailChallenge } from "@/components/verify/DownloadEmailChallenge";
import { cn } from "@/lib/utils";
import { generateUUID } from "@/utils/transform";
import { verificationApi } from "../services/api";

export const Verification = () => {
  const { t } = useTranslation();
  const { envelopeId } = useParams();

  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState("");
  const [inputId, setInputId] = useState(envelopeId || "");
  const [hashInput, setHashInput] = useState("");
  const [verifyMode, setVerifyMode] = useState("id");

  // ============================================
  // TanStack Query - Verify by ID Mutation
  // ============================================
  const verifyByIdMutation = useMutation({
    mutationFn: async (idToVerify) => {
      const res = await verificationApi.verifyEnvelope(idToVerify);
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) {
        setVerificationResult(data);
      } else {
        setError(t("verification.verificationFailed"));
      }
    },
    onError: (err) => {
      setError(
        err.response?.data?.error?.message ||
          t("verification.verificationFailed"),
      );
    },
  });

  // ============================================
  // TanStack Query - Verify by Hash Mutation
  // ============================================
  const verifyByHashMutation = useMutation({
    mutationFn: async (hash) => {
      // First, verify by hash to find the envelope
      const hashRes = await verificationApi.verifyByHash(hash);
      const hashData = hashRes?.data?.data;

      if (!hashData) {
        throw new Error(t("verification.verificationFailed"));
      }

      if (!hashData.isValid || !hashData.envelope?.id) {
        // Hash not found or invalid
        return {
          isValid: false,
          message: hashData.message || t("verification.hashNotFound"),
          verifiedAt: hashData.verifiedAt || new Date().toISOString(),
        };
      }

      // Hash found - now get full envelope verification details
      try {
        const fullRes = await verificationApi.verifyEnvelope(
          hashData.envelope.id,
        );
        const fullData = fullRes?.data?.data;

        if (fullData) {
          return {
            ...fullData,
            verifiedViaHash: true,
            matchedHash: hash,
          };
        }
      } catch {
        // If full verification fails, still return basic hash result
      }

      // Fallback to basic hash result
      return {
        isValid: true,
        envelope: hashData.envelope,
        verifiedViaHash: true,
        matchedHash: hash,
        verifiedAt: hashData.verifiedAt || new Date().toISOString(),
      };
    },
    onSuccess: (data) => {
      setVerificationResult(data);
    },
    onError: (err) => {
      setError(
        err.response?.data?.error?.message ||
          err.message ||
          t("verification.verificationFailed"),
      );
    },
  });

  const loading =
    verifyByIdMutation.isPending || verifyByHashMutation.isPending;

  const handleVerify = () => {
    const idToVerify = inputId.trim();

    if (!idToVerify) {
      setError(t("verification.enterDocumentId"));
      return;
    }

    setError("");
    setVerificationResult(null);
    verifyByIdMutation.mutate(idToVerify);
  };

  const handleVerifyByHash = () => {
    const hash = hashInput.trim();

    if (!hash) {
      setError(t("verification.enterDocumentHash"));
      return;
    }

    setError("");
    setVerificationResult(null);
    verifyByHashMutation.mutate(hash);
  };

  // Downloads require an email challenge (must match a party to the
  // envelope); remember a verified email so the second download skips it
  const [challengeType, setChallengeType] = useState(null);
  const [verifiedEmail, setVerifiedEmail] = useState(null);

  const performDownload = async (type, email) => {
    const { id } = verificationResult.envelope;
    const res =
      type === "document"
        ? await verificationApi.getSignedDocument(id, email)
        : await verificationApi.getCertificate(id, email);

    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      type === "document"
        ? `${verificationResult.envelope?.title || "document"}_signed.pdf`
        : `certificate_${verificationResult.certificate?.number || "document"}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    setVerifiedEmail(email);
  };

  const requestDownload = async (type) => {
    if (!verificationResult?.envelope?.id) return;
    if (verifiedEmail) {
      try {
        await performDownload(type, verifiedEmail);
        return;
      } catch {
        setVerifiedEmail(null);
      }
    }
    setChallengeType(type);
  };

  const handleDownloadCertificate = () => requestDownload("certificate");

  const handleDownloadDocument = () => requestDownload("document");

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header with Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Logo size="xl" showText text={t("common.appName")} />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {t("verification.title")}
          </h1>
          <p className="text-muted-foreground">{t("verification.subtitle")}</p>
        </div>

        {/* Verification Form */}
        {!verificationResult && (
          <Card>
            <CardContent className="p-6">
              {/* Mode Tabs */}
              <div className="flex border-b border-border mb-6">
                <button
                  onClick={() => {
                    setVerifyMode("id");
                    setError("");
                  }}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                    verifyMode === "id"
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  <FileSearch className="w-4 h-4" />
                  {t("verification.verifyById")}
                </button>
                <button
                  onClick={() => {
                    setVerifyMode("hash");
                    setError("");
                  }}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                    verifyMode === "hash"
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Hash className="w-4 h-4" />
                  {t("verification.verifyByHash")}
                </button>
              </div>

              {verifyMode === "id" ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="documentId">
                      {t("verification.documentId")}
                    </Label>
                    <Input
                      id="documentId"
                      value={inputId}
                      onChange={(e) => setInputId(e.target.value)}
                      placeholder={t("verification.enterIdPlaceholder")}
                      className="h-12"
                    />
                    <p className="text-sm text-muted-foreground">
                      {t("verification.idHint")}
                    </p>
                  </div>
                  <Button
                    onClick={handleVerify}
                    loading={loading}
                    className="w-full h-11"
                  >
                    {t("verification.verifyDocument")}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="documentHash">
                      {t("verification.documentHash")}
                    </Label>
                    <Input
                      id="documentHash"
                      value={hashInput}
                      onChange={(e) => setHashInput(e.target.value)}
                      placeholder={t("verification.enterHashPlaceholder")}
                      className="h-12 font-mono text-sm"
                    />
                    <p className="text-sm text-muted-foreground">
                      {t("verification.hashHint")}
                    </p>
                  </div>
                  <Button
                    onClick={handleVerifyByHash}
                    loading={loading}
                    className="w-full h-11"
                  >
                    {t("verification.verifyByHash")}
                  </Button>
                </div>
              )}

              {error && (
                <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                  {error}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {verificationResult && (
          <div className="space-y-4">
            {/* Status Banner */}
            <div
              className={cn(
                "p-6 rounded-xl",
                verificationResult.isValid
                  ? "bg-success/10 border border-success/20"
                  : "bg-destructive/10 border border-destructive/20",
              )}
            >
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    "w-16 h-16 rounded-full flex items-center justify-center",
                    verificationResult.isValid
                      ? "bg-success/20"
                      : "bg-destructive/20",
                  )}
                >
                  {verificationResult.isValid ? (
                    <Check className="w-8 h-8 text-success" />
                  ) : (
                    <X className="w-8 h-8 text-destructive" />
                  )}
                </div>
                <div>
                  <h2
                    className={cn(
                      "text-xl font-bold",
                      verificationResult.isValid
                        ? "text-success"
                        : "text-destructive",
                    )}
                  >
                    {verificationResult.isValid
                      ? t("verification.result.verified")
                      : t("verification.result.failed")}
                  </h2>
                  <p
                    className={
                      verificationResult.isValid
                        ? "text-success/80"
                        : "text-destructive/80"
                    }
                  >
                    {verificationResult.isValid
                      ? t("verification.result.verifiedMessage")
                      : verificationResult.message ||
                        t("verification.result.failedMessage")}
                  </p>
                  {verificationResult.verifiedViaHash && (
                    <Badge variant="outline" className="mt-2">
                      <Hash className="w-3 h-3 mr-1" />
                      {t("verification.verifiedViaHash")}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Document Details - Only show if we have envelope data */}
            {verificationResult?.envelope && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-foreground mb-4">
                    {t("verification.details.documentDetails")}
                  </h3>
                  <dl className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm text-muted-foreground">
                        {t("verification.details.title")}
                      </dt>
                      <dd className="font-medium text-foreground">
                        {verificationResult.envelope?.title || "-"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">
                        {t("verification.details.status")}
                      </dt>
                      <dd>
                        <Badge
                          variant={
                            verificationResult.envelope?.status === "completed"
                              ? "success"
                              : "muted"
                          }
                        >
                          {verificationResult.envelope?.status || "-"}
                        </Badge>
                      </dd>
                    </div>
                    {verificationResult.envelope?.createdAt && (
                      <div>
                        <dt className="text-sm text-muted-foreground">
                          {t("verification.details.created")}
                        </dt>
                        <dd className="font-medium text-foreground">
                          {new Date(
                            verificationResult.envelope.createdAt,
                          ).toLocaleDateString()}
                        </dd>
                      </div>
                    )}
                    {verificationResult.envelope?.completedAt && (
                      <div>
                        <dt className="text-sm text-muted-foreground">
                          {t("verification.details.completed")}
                        </dt>
                        <dd className="font-medium text-foreground">
                          {new Date(
                            verificationResult.envelope.completedAt,
                          ).toLocaleDateString()}
                        </dd>
                      </div>
                    )}
                  </dl>
                </CardContent>
              </Card>
            )}

            {/* Certificate */}
            {verificationResult.certificate && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-foreground mb-4">
                    {t("verification.certificate.title")}
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t("verification.certificate.number")}
                      </p>
                      <p className="font-mono text-foreground">
                        {verificationResult.certificate.number}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadCertificate}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {t("verification.certificate.download")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trusted Timestamp */}
            {verificationResult.timestamp && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    {t("verification.timestamp.title")}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {t("verification.timestamp.authority")}
                        </p>
                        <p className="font-medium text-foreground">
                          {verificationResult.timestamp.authority ||
                            t("verification.systemClock")}
                        </p>
                      </div>
                      <Badge
                        variant={
                          verificationResult.timestamp.rfc3161Compliant
                            ? "success"
                            : "secondary"
                        }
                      >
                        {verificationResult.timestamp.rfc3161Compliant
                          ? t("verification.timestamp.rfc3161")
                          : t("verification.timestamp.systemClock")}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {t("verification.timestamp.time")}
                        </p>
                        <p className="font-medium text-foreground">
                          {verificationResult.timestamp.timestamp
                            ? new Date(
                                verificationResult.timestamp.timestamp,
                              ).toLocaleString()
                            : "-"}
                        </p>
                      </div>
                      {verificationResult.timestamp.documentHash && (
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {t("verification.timestamp.hash")}
                          </p>
                          <p className="font-mono text-sm text-foreground truncate">
                            {verificationResult.timestamp.documentHash}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Verification */}
            {verificationResult.verification && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-foreground mb-4">
                    {t("verification.quickVerify.title")}
                  </h3>
                  <div className="flex items-center gap-6">
                    {verificationResult.verification.qrCodeUrl && (
                      <div className="shrink-0">
                        <img
                          src={verificationResult.verification.qrCodeUrl}
                          alt="Verification QR Code"
                          className="w-24 h-24 border rounded"
                        />
                        <p className="text-xs text-center text-muted-foreground mt-1">
                          {t("verification.quickVerify.scanQR")}
                        </p>
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="mb-3">
                        <p className="text-sm text-muted-foreground">
                          {t("verification.quickVerify.code")}
                        </p>
                        <p className="font-mono text-lg font-bold text-foreground">
                          {verificationResult.verification.shortCode}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {t("verification.quickVerify.url")}
                        </p>
                        <a
                          href={verificationResult.verification.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline break-all"
                        >
                          {verificationResult.verification.url}
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Signers */}
            {verificationResult.signers &&
              verificationResult.signers.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4">
                      {t("verification.signers.title")}
                    </h3>
                    <div className="space-y-3">
                      {verificationResult.signers.map((signer) => (
                        <div
                          key={generateUUID()}
                          className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                        >
                          <div
                            className={cn(
                              "w-10 h-10 rounded-full flex items-center justify-center",
                              signer.status === "signed"
                                ? "bg-success/10 text-success"
                                : "bg-muted text-muted-foreground",
                            )}
                          >
                            {signer.status === "signed" ? (
                              <Check className="w-5 h-5" />
                            ) : (
                              <Circle className="w-5 h-5" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-foreground">
                              {signer.name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {signer.email}
                            </p>
                          </div>
                          {signer.signedAt && (
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">
                                {t("verification.signers.signed")}
                              </p>
                              <p className="text-sm font-medium text-foreground">
                                {new Date(signer.signedAt).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

            {/* Document Integrity */}
            {verificationResult.documents &&
              verificationResult.documents.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4">
                      {t("verification.integrity.title")}
                    </h3>
                    <div className="space-y-3">
                      {verificationResult.documents.map((doc) => (
                        <div
                          key={generateUUID()}
                          className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                        >
                          <div
                            className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center",
                              doc.isValid
                                ? "bg-success/10 text-success"
                                : "bg-destructive/10 text-destructive",
                            )}
                          >
                            {doc.isValid ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground">
                              {doc.documentName}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono truncate">
                              {t("verification.integrity.hash")}:{" "}
                              {doc.currentHash?.substring(0, 32)}...
                            </p>
                          </div>
                          <Badge
                            variant={doc.isValid ? "success" : "danger"}
                            size="sm"
                          >
                            {doc.isValid
                              ? t("verification.integrity.verified")
                              : t("verification.integrity.modified")}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

            {/* Audit Summary */}
            {verificationResult.auditSummary && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-foreground mb-4">
                    {t("verification.audit.title")}
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    {t("verification.audit.eventsRecorded", {
                      count: verificationResult.auditSummary.totalEvents,
                    })}
                  </p>
                  {verificationResult.auditSummary.firstEvent && (
                    <div className="text-sm text-muted-foreground">
                      <span>
                        {t("verification.audit.first")}:{" "}
                        {verificationResult.auditSummary.firstEvent.action}
                      </span>
                      {" • "}
                      <span>
                        {t("verification.audit.last")}:{" "}
                        {verificationResult.auditSummary.lastEvent.action}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                onClick={() => setVerificationResult(null)}
                variant="outline"
              >
                {t("verification.verifyAnother")}
              </Button>
              {verificationResult?.isValid && verificationResult?.envelope && (
                <Button onClick={handleDownloadDocument}>
                  <Download className="w-4 h-4 mr-2" />
                  {t("verification.downloadSigned")}
                </Button>
              )}
            </div>

            {/* Verification Time */}
            {verificationResult?.verifiedAt && (
              <p className="text-center text-sm text-muted-foreground">
                {t("verification.verifiedAt")}:{" "}
                {new Date(verificationResult.verifiedAt).toLocaleString()}
              </p>
            )}
          </div>
        )}

        <DownloadEmailChallenge
          open={!!challengeType}
          onClose={() => setChallengeType(null)}
          onSubmit={(email) => performDownload(challengeType, email)}
        />

        {/* Info */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>{t("verification.info.hashMethod")}</p>
          <p className="mt-1">{t("verification.info.tamperWarning")}</p>
        </div>
      </div>
    </div>
  );
};

export default Verification;
